import React, { useEffect, useState } from "react";
import {
  X,
  Download,
  Image as ImageIcon,
  Play,
  ZoomIn,
  ZoomOut,
  Maximize2,
} from "lucide-react";

const MediaPreviewModal = ({
  isOpen,
  onClose,
  mediaUrl,
  mediaType = "image",
  mediaAlt = "Media",
  showDownload = true,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;
  const ZOOM_STEP = 0.5;

  // Reset zoom and pan when modal opens or media changes
  useEffect(() => {
    if (isOpen) {
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen, mediaUrl]);

  // Reset pan position when zoom level changes to 1
  useEffect(() => {
    if (zoomLevel === 1) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  // Debug: Log when modal props change
  useEffect(() => {
    console.log("🎭 MediaPreviewModal props changed:", {
      isOpen,
      mediaUrl,
      mediaType,
      mediaAlt,
    });
  }, [isOpen, mediaUrl, mediaType, mediaAlt]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    console.log("🚫 MediaPreviewModal not rendering - isOpen:", isOpen);
    return null;
  }

  console.log("✅ MediaPreviewModal rendering with:", {
    isOpen,
    mediaUrl,
    mediaType,
  });

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Pan/Drag handlers for desktop
  const handleMouseDown = (e) => {
    if (zoomLevel > 1 && mediaType === "image") {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y,
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Pan/Drag handlers for mobile
  const handleTouchStart = (e) => {
    if (zoomLevel > 1 && mediaType === "image") {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - panPosition.x,
        y: touch.clientY - panPosition.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const touch = e.touches[0];
      setPanPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
      e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        mediaAlt || `media.${mediaType === "video" ? "mp4" : "png"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download media:", error);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderMedia = () => {
    if (mediaType === "video") {
      return (
        <video
          src={mediaUrl}
          controls
          className="w-full h-auto max-w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg shadow-lg"
          style={{ minHeight: "200px" }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        >
          Your browser does not support video playback.
        </video>
      );
    } else {
      return (
        <img
          src={mediaUrl}
          alt={mediaAlt}
          className="object-contain rounded-lg shadow-lg select-none"
          style={{
            maxWidth: zoomLevel > 1 ? "none" : "100%",
            maxHeight: "80vh",
            width: "auto",
            height: "auto",
            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.2s ease-out",
            cursor:
              zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
          draggable={false}
        />
      );
    }
  };

  const getMediaIcon = () => {
    return mediaType === "video" ? Play : ImageIcon;
  };

  const MediaIcon = getMediaIcon();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Mobile: Full screen layout */}
      <div className="sm:hidden h-full flex flex-col bg-black">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-black/90 backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <MediaIcon className="w-6 h-6 text-white" />
            <h3 className="text-lg font-medium text-white truncate">
              {mediaAlt}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            {mediaType === "image" && (
              <>
                <button
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= MIN_ZOOM}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white text-sm font-medium min-w-[3rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= MAX_ZOOM}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Zoom In"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                {zoomLevel !== 1 && (
                  <button
                    onClick={handleResetZoom}
                    className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Reset Zoom"
                  >
                    <Maximize2 className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
            {showDownload && (
              <button
                onClick={handleDownload}
                className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                title={`Download ${mediaType}`}
              >
                <Download className="w-6 h-6" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Media Container - Mobile */}
        <div
          className={`flex-1 flex items-center justify-center p-2 sm:p-4 ${
            zoomLevel > 1 ? "overflow-auto" : "overflow-hidden"
          }`}
          onClick={handleBackdropClick}
        >
          {renderMedia()}
          {/* Fallback for broken media */}
          <div className="hidden items-center justify-center flex-col text-white/60">
            <MediaIcon className="w-16 h-16 mb-4" />
            <span className="text-lg">
              {mediaType === "video" ? "Video" : "Image"} unavailable
            </span>
          </div>
        </div>
      </div>

      {/* Desktop: Modal layout */}
      <div className="hidden sm:flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black/75 backdrop-blur-sm transition-opacity"
          onClick={handleBackdropClick}
        />

        {/* Modal panel */}
        <div
          className={`relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-h-[90vh] w-full overflow-hidden ${
            mediaType === "video" ? "max-w-5xl" : "max-w-4xl"
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="flex items-center space-x-3">
              <MediaIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                {mediaAlt}
              </h3>
            </div>
            <div className="flex items-center space-x-2">
              {mediaType === "image" && (
                <>
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= MIN_ZOOM}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>
                  <span className="text-gray-700 dark:text-gray-300 text-sm font-medium min-w-[3rem] text-center">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= MAX_ZOOM}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                  {zoomLevel !== 1 && (
                    <button
                      onClick={handleResetZoom}
                      className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Reset Zoom"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </button>
                  )}
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600"></div>
                </>
              )}
              {showDownload && (
                <button
                  onClick={handleDownload}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title={`Download ${mediaType}`}
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Media Container - Desktop */}
          <div
            className={`flex items-center justify-center p-4 sm:p-6 bg-gray-50 dark:bg-gray-800 min-h-[60vh] max-h-[80vh] ${
              zoomLevel > 1 ? "overflow-auto" : "overflow-hidden"
            }`}
          >
            {renderMedia()}
            {/* Fallback for broken media */}
            <div className="hidden items-center justify-center flex-col text-gray-400">
              <MediaIcon className="w-16 h-16 mb-4" />
              <span className="text-lg">
                {mediaType === "video" ? "Video" : "Image"} unavailable
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaPreviewModal;
